import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { Link, withRouter } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';

import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import * as NotificationActions from 'containers/Notification/actions';
import DeedsPageBanner from 'assets/pageBanners/deeds.avif';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import { selectDeedsCreatedByEmployeesForApprove } from 'entities/deed/selectors';
import ButtonBadge from 'retired/ButtonBadge';
import Box from 'retired/Box';
import { Features } from 'utils/features';
import { lightGray, darkPurple, white } from 'utils/color-styles';
import { Privileges } from 'utils/privileges';

const MainButton = ({
  text,
  onClick,
  dropdown,
  fill,
}: {
  text: string;
  onClick: () => void;
  dropdown?: boolean;
  fill?: boolean;
}) => (
  <Button
    color="primary"
    variant="outlined"
    size="large"
    onClick={onClick}
    style={{
      lineHeight: '24px',
      backgroundColor: fill ? darkPurple : 'transparent',
      color: fill ? white : darkPurple,
      marginLeft: 10,
    }}
  >
    {text}
    {dropdown && <ArrowDropDownIcon />}
  </Button>
);

const DropdownLink = ({ to, text }: { to: string; text: string }) => (
  <Link to={to}>
    <MenuItem style={{ borderTop: `1px solid ${lightGray}` }}>{text}</MenuItem>
  </Link>
);

const Banner = ({ deedsCreatedByEmployeesForApprove, actions, history, match, deedType }) => {
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);

  const isCompany = !!organization?.isCompany();

  const isActionsFeatureEnabled = user?.hasFeature(Features.ACTIONS);

  const routeMatchType = match.params.type;

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  return (
    <PageBannerContainer color="#E0E6F5" backgroundImage={DeedsPageBanner} backgroundSize="500px">
      <h1>Deeds</h1>
      <div style={{ marginLeft: 'auto' }}>
        {organization && !organization.isValid() ? (
          <Button
            color="primary"
            onClick={() =>
              actions.showErrorAction('Please fill out organization information in settings first')
            }
          >
            Create New Deed
          </Button>
        ) : (
          <div
            style={{
              textAlign: 'right',
              display: 'flex',
              alignItems: 'middle',
            }}
          >
            {user?.hasFeature(Features.USER_DRAFT_DEED) && (
              <ButtonBadge
                badgeContent={deedsCreatedByEmployeesForApprove?.size || null}
                color="primary"
              >
                <MainButton
                  onClick={() => history.push('/deeds/submitted')}
                  text="Approve Employee Deeds"
                />
              </ButtonBadge>
            )}

            {isActionsFeatureEnabled && user?.hasPrivilege(Privileges.ACTIONS_MANAGE) && (
              <MainButton
                onClick={() => history.push('/actions/templates/browse')}
                text="Browse Actions Library"
              />
            )}

            {user?.hasFeature(Features.BROWSING_EXTERNAL_OPPORTUNITIES) && (
              <MainButton
                onClick={() => history.push('/deeds/browse')}
                text="Browse Opportunities"
              />
            )}

            <MainButton
              onClick={
                isCompany ? handleClick : () => history.push(`/deeds/${routeMatchType}/edit/new`)
              }
              text="Add Deeds"
              dropdown={isCompany}
              fill
            />

            {isCompany && (
              <Popper open={!!anchorEl} anchorEl={anchorEl} style={{ marginTop: 5 }}>
                <Box style={{ padding: 0 }}>
                  <ClickAwayListener onClickAway={() => handleClick(false)}>
                    <MenuList disablePadding>
                      {user?.hasFeature(Features.VOLUNTEERING) &&
                        user?.hasPrivilege(Privileges.EVENT_MANAGE) && (
                          <DropdownLink
                            to="/deeds/volunteer/edit/new"
                            text="Create Volunteer Event"
                          />
                        )}
                      {user?.hasFeature(Features.EVENTS) &&
                        user?.hasPrivilege(Privileges.BASE_EVENT_MANAGE) && (
                          <DropdownLink to="/deeds/events/edit/new" text="Create Event" />
                        )}

                      {user?.canDonate() &&
                        user?.hasFeature(Features.FUNDRAISERS) &&
                        user?.hasPrivilege(Privileges.FUNDRAISER_MANAGE) && (
                          <DropdownLink to="/deeds/campaigns/edit/new" text="Create Fundraiser" />
                        )}

                      {user?.hasFeature(Features.VOLUNTEERING) &&
                        user?.hasPrivilege(Privileges.PROJECT_MANAGE) && (
                          <DropdownLink to="/deeds/projects/edit/new" text="Create Project" />
                        )}

                      {isActionsFeatureEnabled && user?.hasPrivilege(Privileges.ACTIONS_MANAGE) && (
                        <>
                          <DropdownLink to="/actions/new" text="Create Action" />
                          <DropdownLink to="/actions/template/new" text="Create Action Template" />
                        </>
                      )}
                      {deedType === 'Event' && (
                        <DropdownLink
                          to={`/deeds/${routeMatchType}/edit/past`}
                          text="Add a Past Deed"
                        />
                      )}
                    </MenuList>
                  </ClickAwayListener>
                </Box>
              </Popper>
            )}
          </div>
        )}
      </div>
    </PageBannerContainer>
  );
};

const withConnect = connect(
  (state) => ({
    deedsCreatedByEmployeesForApprove: selectDeedsCreatedByEmployeesForApprove(state),
  }),
  (dispatch) => ({
    actions: bindActionCreators({ ...NotificationActions }, dispatch),
  })
);

export default compose(withRouter, withConnect)(Banner);
